import {GuideValues, StudioMode, StudioViewPositions} from '../../../configs/studio';

const studioStorageKey = 'studio.settings';

interface StorageStudioSettings {
  fontSize: number;
  isOpenSettingsPanel: boolean;
  guide: GuideValues;
  averageCalibrateVolume: number;
  maxMeasuredLevel: number;
  mainContainerHeight: number;
  startDelayTime: number;
  mediaNotAvailable: boolean;
  ownerUserSettings: {
    [key: string]: StudioSettingsByUser;
  };
  studioTopView: StudioViewPositions;
  studioBottomView: StudioViewPositions;
  studioPrompterSync: boolean;
  studioMode: StudioMode;
  selectedCameraId: string;
  selectedAudioId: string;
}

interface StudioSettingsByUser {
  scrollSpeed: number;
  autoScrollAudioService: boolean;
}

type SettingKey = keyof Omit<StorageStudioSettings, 'ownerUserSettings' | 'notifications'>;
type SettingKeyByUser = keyof StudioSettingsByUser;

export const defaultSettingsByUser: StudioSettingsByUser = {
  scrollSpeed: 3.2,
  autoScrollAudioService: false,
};

export const defaultSettings: StorageStudioSettings = {
  guide: GuideValues.Top,
  isOpenSettingsPanel: true,
  fontSize: 24,
  startDelayTime: 0,
  averageCalibrateVolume: 0,
  maxMeasuredLevel: 0,
  mainContainerHeight: 0,
  ownerUserSettings: {},
  mediaNotAvailable: false,
  studioBottomView: StudioViewPositions.CAMERA,
  studioTopView: StudioViewPositions.TELEPROMPTER,
  studioMode: StudioMode.TELEPROMPTER,
  studioPrompterSync: true,
  selectedCameraId: '',
  selectedAudioId: '',
};

export const getSettings = (): StorageStudioSettings => {
  const storedSettings = localStorage.getItem(studioStorageKey);
  if (storedSettings) {
    try {
      const settings = JSON.parse(storedSettings);

      // remove old format
      if (settings.guide === undefined) {
        settings.guide = GuideValues.Top;
      }
      if (settings.guide === true || settings.guide === 1) {
        settings.guide = GuideValues.Top;
      }
      if (settings.guide === false || settings.guide === 0) {
        settings.guide = GuideValues.Off;
      }

      return settings;
    } catch (e) {
      return defaultSettings;
    }
  }
  return defaultSettings;
};

export const getSettingsByOwnerUser = (ownerUser: string): StudioSettingsByUser => {
  const settings = getSettings();

  try {
    if (settings.ownerUserSettings && settings.ownerUserSettings[ownerUser]) {
      return settings.ownerUserSettings[ownerUser];
    }

    if (settings.ownerUserSettings) {
      settings.ownerUserSettings[ownerUser] = defaultSettingsByUser;
    } else {
      settings.ownerUserSettings = { [ownerUser]: defaultSettingsByUser };
    }

    localStorage.setItem(studioStorageKey, JSON.stringify(settings));

    return defaultSettingsByUser;
  } catch (e) {
    const defaultStudioSettings = defaultSettings;
    defaultStudioSettings.ownerUserSettings[ownerUser] = defaultSettingsByUser;
    localStorage.setItem(studioStorageKey, JSON.stringify(defaultStudioSettings));

    return defaultSettingsByUser;
  }
};

export const saveSettingsByUser = (ownerUser: string, key: SettingKeyByUser, value: any): void => {
  const storage = getSettings();

  if (storage.ownerUserSettings && storage.ownerUserSettings[ownerUser]) {
    storage.ownerUserSettings[ownerUser] = { ...storage.ownerUserSettings[ownerUser], [key]: value };
  } else if (storage.ownerUserSettings) {
    storage.ownerUserSettings[ownerUser] = { ...defaultSettingsByUser, [key]: value };
  } else {
    storage.ownerUserSettings = { [ownerUser]: { ...defaultSettingsByUser, [key]: value } };
  }

  localStorage.setItem(studioStorageKey, JSON.stringify(storage));
};

export const saveSettings = (key: SettingKey, value: any): void => {
  const storage = getSettings();
  storage[key] = value as never;
  localStorage.setItem(studioStorageKey, JSON.stringify(storage));
};
